import Vimeo, { EndEvent, PlayEvent, TimeUpdateEvent } from '@u-wave/react-vimeo';
import { css } from 'aphrodite/no-important';
import { CustomIdleTimerContext } from 'hooks';
import useAchievements, { CATEGORIES } from 'hooks/useAchievements';
import React, { FC, IframeHTMLAttributes, useContext, useEffect, useRef, useState } from 'react';
import { trackVideoComplete, trackVideoPlay, trackVideoProgress, trackVideoTimeWatched } from 'util/google-helpers';
import { style } from './style';

const VIDEO_TIME_WATCHED_INTERVAL = 10; // frequency which to log time watched (in seconds)
const CLEVER_CAST_REGEX = /player.clevercast.com/;
const CLEVER_CAST_IS_EVENT_REGEX = /(player.clevercast.com).*(event_id)/;
const CLEVER_CAST_EVENT_ID_REGEX = /event_id=([a-zA-Z0-9]{6})+/;
const GENERIC_IS_EVENT_REGEX = /event/;
const GENERIC_EVENT_ID_REGEX = /\d+/;

const isLiveStream = (source: string): boolean => {
  const isClevercastSource = RegExp(CLEVER_CAST_REGEX).test(source);
  if (isClevercastSource) return RegExp(CLEVER_CAST_IS_EVENT_REGEX).test(source);
  return RegExp(GENERIC_IS_EVENT_REGEX).test(source);
};

const getVideoIdFromSource = (source: string): string => {
  const isClevercastSource = RegExp(CLEVER_CAST_REGEX).test(source);
  if (isClevercastSource) {
    const clevercastVideoIdMatches = RegExp(CLEVER_CAST_EVENT_ID_REGEX).exec(source);
    return (clevercastVideoIdMatches && clevercastVideoIdMatches[1]) || '';
  }
  const genericMatches = RegExp(GENERIC_EVENT_ID_REGEX).exec(source);
  return (genericMatches && genericMatches[0]) || '';
};
interface Props extends IframeHTMLAttributes<HTMLIFrameElement> {
  id?: string;
  options: any;
  src: string;
  data: any;
  autoplay?: boolean;
  onEndCallback?: () => any;
}

/**
 * Responsive iframe
 * @param {Object} props
 */

export const VideoPlayer: FC<Props> = (props: Props) => {
  const { options, src, autoplay = false } = props;
  const { title } = options;
  const { video_achievement_id: videoAchievementId } = props.data;
  // const styles = style({ aspectRatio });
  const shouldAutoplay = RegExp('autoplay').test(src) || autoplay;
  const isLivestream = isLiveStream(src);
  const videoId = getVideoIdFromSource(src);
  const videoUrl = src.split('?')[0];
  const progress = useRef<number>(0); // keeps track of what percent progress has played
  const hasPlayed = useRef<boolean>(false); // flag to check if the video started playing (so we only fire a video play event once)
  const secondsWatched = useRef<number>(0); // keeps track of the number of seconds the user has watched the current video
  const currentPosition = useRef<number>(0); // keeps track of the current position of the video
  const videoLabel = title + ' | ' + videoId;
  const { submitAchievement } = useAchievements();
  const [achievementFulfilled, setAchievementFulfilled] = useState(false);
  const resetIdleTimer = useContext(CustomIdleTimerContext);
  const styles = style();

  /**
   * Reset flags and counters when video changes
   */
  useEffect(() => {
    hasPlayed.current = false;
    progress.current = 0;
    secondsWatched.current = 0;
    currentPosition.current = 0;
  }, [videoId]);

  useEffect(() => {
    if (achievementFulfilled && videoAchievementId) {
      submitAchievement(CATEGORIES.VIDEOS, videoAchievementId);
    }
  }, [achievementFulfilled, submitAchievement, videoAchievementId]);

  const onTimeUpdate = (data: TimeUpdateEvent) => {
    const prevProgress = progress.current;
    const currentSeconds = Math.floor(data.seconds);

    // Keep track of percentage watched. Fires progress events at 2%, 25%, 50%, 75%
    if (data.percent >= 0.02) {
      setAchievementFulfilled(true);
    }

    if (data.percent >= 0.02 && progress.current < 0.02) {
      progress.current = 0.02;
    } else if (data.percent >= 0.25 && progress.current < 0.25) {
      progress.current = 0.25;
    } else if (data.percent >= 0.5 && progress.current < 0.5) {
      progress.current = 0.5;
    } else if (data.percent >= 0.75 && progress.current < 0.75) {
      progress.current = 0.75;
    }
    if (prevProgress !== progress.current) {
      resetIdleTimer();
      trackVideoProgress(videoLabel, progress.current);
    }

    // Keep track of seconds watched. Fire a time watched event at a set interval
    if (currentPosition.current !== currentSeconds) {
      currentPosition.current = currentSeconds;
      secondsWatched.current++;
      if (secondsWatched.current % VIDEO_TIME_WATCHED_INTERVAL === 0) {
        trackVideoTimeWatched(videoLabel, VIDEO_TIME_WATCHED_INTERVAL);
      }
    }
  };

  const onPlay = (data: PlayEvent) => {
    if (!hasPlayed.current) {
      resetIdleTimer();
      trackVideoPlay(videoLabel);
      hasPlayed.current = true;
    }
  };

  /**
   * When the video ends, reset the progress and play events to track again if user replays video
   * @param data
   */
  const onEnd = (data: EndEvent) => {
    hasPlayed.current = false;
    progress.current = 0;
    secondsWatched.current = 0;
    resetIdleTimer();
    trackVideoComplete(videoLabel);
    if (props.onEndCallback) props.onEndCallback();
  };

  return isLivestream ? (
    <div className={css(styles.videoEmbed)}>
      <iframe
        src={src}
        title={videoId}
        frameBorder='0'
        allow='autoplay; fullscreen; picture-in-picture'
        allowFullScreen></iframe>
    </div>
  ) : (
    <Vimeo
      responsive
      video={videoUrl}
      autoplay={shouldAutoplay}
      onTimeUpdate={onTimeUpdate}
      onPlay={onPlay}
      onEnd={onEnd}></Vimeo>
  );
};
